import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { getMaxQuestions } from "src/hooks/useAppConfig";
import { useCopyCourse, useEditCourse, useRemoveCourses } from "src/hooks/useCourses";
import { useCreateExam } from "src/hooks/useExams";
import CopyExamIcon from "src/images/Icons/newIcons/copy.svg";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import ChangeNameIcon from "src/images/Icons/newIcons/edit.svg";
import GenerateExamIcon from "src/images/Icons/newIcons/fileClose.svg";
import FolderIcon from "src/images/Icons/newIcons/folder.svg";
import GenerateExamFromWrongAnswersIcon from "src/images/Icons/newIcons/graduationCap.svg";
import PlayIcon from "src/images/Icons/newIcons/play.svg";
import newApi from "src/newApi";
import DefaultActions from "./DefaultActions";
import type { ActionItem } from "./types";
import { useFolderItemActions } from "./useFolderItemActions";

export default function CourseActions({ row }: { row: any }) {
	const { openModal } = useModals();
	const { t } = useTranslation();
	const { mutateAsync: generateExamAsync } = useCreateExam() as { mutateAsync: (data: unknown) => Promise<unknown> };
	const { mutateAsync: copyCourse } = useCopyCourse() as { mutateAsync: (data: unknown) => Promise<{ id: string }> };
	const folderActions: ActionItem = useFolderItemActions(row, "course");
	const { mutateAsync: removeCourses } = useRemoveCourses() as unknown as {
		mutateAsync: (ids: string[]) => Promise<void>;
	};

	const { mutateAsync: renameCourse } = useEditCourse() as unknown as {
		mutateAsync: (payload: { id: string; name: string }) => Promise<unknown>;
	};
	const history = useHistory();
	const getCourse = () =>
		history.push(generatePath(routing.paths.course, { id: row.id }));

	const details = {
		finishedExamsIds: row.lessons
			.filter(({ status }: any) => status === "resolved")
			.map(({ itemId }: any) => ({ id: itemId })),
		unfinishedExamsIds: row.lessons
			.filter(({ status }: any) => status === "incomplete")
			.map(({ itemId }: any) => ({ id: itemId })),
		additionalOption: {
			wrongAnswers: true,
		},
		name: `${t("defaultNames.wrongAnswers")} - ${dayjs(new Date()).format("DD-MM-YYYY")}`,
	};

	const generateExam = async () => {
		if (
			!details.finishedExamsIds.length &&
			!details.unfinishedExamsIds.length
		) {
			toast.error(t("errors.emptyWrongAnswersInCourse"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 5000,
			});
			return;
		}

		const res = await newApi.getCount(details);
		const maxQuestions = getMaxQuestions();
		if (res > maxQuestions) {
			toast.error(t("errors.toManyQuestionsInExamCourse", { maxQuestions }), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 7000,
			});
			return;
		}

		await generateExamAsync(details);
	};

	const divider = { isDivider: true as const };

	const defaultItems: (ActionItem | { isDivider: true } | false)[] = [
		{
			title: t("menuActions.course.resume"),
			onClick: getCourse,
			Icon: <PlayIcon className="icon-normal" />,
		},
		{
			title: t("menuActions.course.edit"),
			onClick: () =>
				openModal(ModalsNames.ChangeNameModal, {
					title: t("modals.changeName.course.title", "Zmień nazwę kursu"),
					label: t("modals.changeName.course.label", "Nazwa kursu"),
					placeholder: t("modals.changeName.course.placeholder", "Wpisz nazwę kursu"),
					name: row.name,
					onSave: (name: string) => renameCourse({ id: row.id, name }),
				}),
			Icon: <ChangeNameIcon className="icon-normal" />,
		},
		{
			title: t("menuActions.course.copyCourse"),
			onClick: async () => {
				const { id } = await copyCourse({
					courseId: row.id,
				});

				const path = generatePath(routing.paths.course, { id });
				return history.push(path);
			},
			Icon: <CopyExamIcon className="icon-small icon-black" />,
		},
		divider,
		{
			title: t("menuActions.course.generateExamFromWrongAnswers"),
			onClick: () => generateExam(),
			Icon: <GenerateExamIcon className="icon-normal" />,
		},
		{
			title: t("menuActions.course.generateCourseFromWrongAnswers"),
			onClick: () =>
				openModal(ModalsNames.GenerateCourseFromWrongAnswersModal, {
					course: row,
				}),
			Icon: <GenerateExamFromWrongAnswersIcon className="icon-normal" />,
		},
		divider,
		!row.folderId && {
			title: t("menuActions.course.moveToFolder", "Przenieś do folderu"),
			testId: `course-move-to-folder-${row.id}`,
			onClick: () => {
				openModal(ModalsNames.MoveToFolderModal, {
					selectedItems: [{ id: row.id, itemId: row.id, itemType: "course" }],
					isCopyMode: true,
					multiSelect: true,
					custom: {
						title: t("folder.moveToFolderTitle", "Przenieś do folderu"),
						actionLabel: t("folder.move", "Przenieś"),
					},
					onSuccess: () => {
						toast.success(
							t("folder.movedToFolderSuccess", "Przeniesiono do folderu"),
						);
					},
				});
			},
			Icon: <FolderIcon className="icon-normal" />,
		},
		(row.folderId ? folderActions : false),
		divider,
		{
			title: t("menuActions.course.remove"),
			className: "list-item-error",
			itemTextClassName: "text-error",
			testId: `course-delete-${row.id}`,
			onClick: () => {
				openModal(ModalsNames.DeleteItemModal, {
					title: t("modals.deleteItem.course.title", "Usunąć kurs?"),
					description: t(
						"modals.deleteItem.course.description",
						"Kurs zostanie trwale usunięty razem z postępem nauki. Tej operacji nie można cofnąć.",
					),
					confirmText: t("modals.deleteItem.course.confirm", "Usuń kurs"),
					itemLabel: row.name,
					onDelete: () => removeCourses([row.id]),
				});
			},
			Icon: <DeleteIcon className="icon-small icon-error" />,
		},
	];

	return <DefaultActions defaultItems={defaultItems} />;
}
